import { useRef, useEffect, useState } from 'react';
import { GRID_WIDTH, GRID_HEIGHT, CELL_SIZE, DIMENSION_COLORS } from '../../editor/EditorTypes';
import { EntityData, WordBoxData } from '../../types';

// Image cache for entity sprites
const imageCache = new Map<string, HTMLImageElement>();

// Preload entity images
const preloadImage = (type: string): Promise<HTMLImageElement> => {
  const cacheKey = type.toLowerCase();
  
  if (imageCache.has(cacheKey)) {
    return Promise.resolve(imageCache.get(cacheKey)!);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = `/assets/${cacheKey}.png`;
    img.onload = () => {
      imageCache.set(cacheKey, img);
      resolve(img);
    };
    img.onerror = () => {
      console.warn(`Failed to load image for ${type}`);
      reject(new Error(`Failed to load ${cacheKey}.png`));
    };
  });
};

interface EditorCanvasProps {
  entities: EntityData[];
  wordBoxes: { [dimension: number]: WordBoxData[] };
  currentDimension: number;
  selectedTool: 'entity' | 'word' | 'erase' | 'select';
  selectedEntity?: string;
  selectedWord?: string;
  onCanvasClick: (x: number, y: number) => void;
  onCanvasRightClick: (x: number, y: number) => void;
}

export function EditorCanvas({
  entities,
  wordBoxes,
  currentDimension,
  selectedTool,
  onCanvasClick,
  onCanvasRightClick
}: EditorCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasWidth = GRID_WIDTH * CELL_SIZE;
  const canvasHeight = GRID_HEIGHT * CELL_SIZE;
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Preload all entity images on mount
  useEffect(() => {
    const entityTypes = [
      'phantom', 'portal', 'barrier', 'orb', 'crystal', 'rock', 'water', 'fire', 'key', 'door',
      'ice', 'lava', 'mirror', 'void', 'bolt', 'star', 'skull', 'coin', 'ghost', 'statue',
      'hunter', 'watcher', 'shadow', 'pursuer', 'sentinel'
    ];

    Promise.all(entityTypes.map(type => preloadImage(type.toUpperCase())))
      .then(() => setImagesLoaded(true))
      .catch(err => console.error('Failed to preload images:', err));
  }, []);

  // Render canvas whenever data changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw grid
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1;
    for (let x = 0; x <= GRID_WIDTH; x++) {
      ctx.beginPath();
      ctx.moveTo(x * CELL_SIZE, 0);
      ctx.lineTo(x * CELL_SIZE, canvasHeight);
      ctx.stroke();
    }
    for (let y = 0; y <= GRID_HEIGHT; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * CELL_SIZE);
      ctx.lineTo(canvasWidth, y * CELL_SIZE);
      ctx.stroke();
    }

    // Render entities (only in dimension 1)
    if (currentDimension === 1 && imagesLoaded) {
      entities.forEach(entity => {
        const img = imageCache.get(entity.type.toLowerCase());
        if (img) {
          ctx.drawImage(img, entity.x * CELL_SIZE, entity.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        } else {
          // Fallback rectangle if image not loaded
          ctx.fillStyle = '#888';
          ctx.fillRect(entity.x * CELL_SIZE, entity.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        }
      });
    }

    // Render word boxes (dimensions 2-4)
    if (currentDimension > 1) {
      const dimensionWords = wordBoxes[currentDimension] || [];
      dimensionWords.forEach(word => {
        const dimColor = DIMENSION_COLORS[currentDimension as keyof typeof DIMENSION_COLORS];
        
        ctx.fillStyle = dimColor;
        ctx.fillRect(
          word.x * CELL_SIZE + 2,
          word.y * CELL_SIZE + 2,
          CELL_SIZE - 4,
          CELL_SIZE - 4
        );
        
        ctx.strokeStyle = dimColor;
        ctx.lineWidth = 2;
        ctx.strokeRect(
          word.x * CELL_SIZE + 2,
          word.y * CELL_SIZE + 2,
          CELL_SIZE - 4,
          CELL_SIZE - 4
        );
        
        ctx.fillStyle = '#000';
        ctx.font = 'bold 8px "Courier New"';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          word.type.replace('WORD_', ''),
          word.x * CELL_SIZE + CELL_SIZE / 2,
          word.y * CELL_SIZE + CELL_SIZE / 2
        );
      });
    }

    // Draw info text
    ctx.fillStyle = '#666';
    ctx.font = '12px "Courier New"';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`Tool: ${selectedTool} | Grid: ${GRID_WIDTH}x${GRID_HEIGHT}`, canvasWidth - 5, canvasHeight - 5);
  }, [entities, wordBoxes, currentDimension, selectedTool, canvasWidth, canvasHeight, imagesLoaded]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) / CELL_SIZE);
    
    if (x >= 0 && x < GRID_WIDTH && y >= 0 && y < GRID_HEIGHT) {
      onCanvasClick(x, y);
    }
  };

  const handleRightClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) / CELL_SIZE);
    
    if (x >= 0 && x < GRID_WIDTH && y >= 0 && y < GRID_HEIGHT) {
      onCanvasRightClick(x, y);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      className="editor-canvas"
      width={canvasWidth}
      height={canvasHeight}
      onClick={handleClick}
      onContextMenu={handleRightClick}
      style={{
        border: '2px solid #333',
        cursor: 'crosshair',
        imageRendering: 'pixelated'
      }}
    />
  );
}
